import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

const NewStarRating = ({
    productDetails,
    prev_rating,
    prev_review,
    itemId,
    primary_color,
    secondary_color,
    primary_highlight_color,
    secondary_highlight_color,
    google_review_link,
    branch_name,
}) => {
    // ============================================
    // NEW SIMPLIFIED STATE FOR OVERALL REVIEW ONLY
    // ============================================
    const [rating, setRating] = useState(0);
    const [reviewText, setReviewText] = useState('');
    const [selectedStar, setSelectedStar] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isRatingSubmitted, setIsRatingSubmitted] = useState(false);
    const [isReviewSubmitted, setIsReviewSubmitted] = useState(false);

    // ============================================
    // PREVIOUS STATE - COMMENTED FOR REFERENCE
    // ============================================
    // const [productRatings, setProductRatings] = useState({});

    // ============================================
    // PREVIOUS LOGIC - COMMENTED FOR REFERENCE
    // ============================================
    // useEffect(() => {
    //     // Initialize product ratings
    //     if (productDetails) {
    //         const initialRatings = {};
    //         productDetails.forEach((product) => {
    //             initialRatings[product.id] = product.service_rating || 0;
    //         });
    //         setProductRatings(initialRatings);
    //     }
    //     // Set initial rating if available
    //     if (prev_rating) {
    //         setRating(prev_rating);
    //         setSelectedStar(prev_rating);
    //         setIsRatingSubmitted(true);
    //     }
    //     // Set initial review text if available
    //     if (prev_review) {
    //         setReviewText(prev_review);
    //         setIsReviewSubmitted(true);
    //     }
    //     // If we have both product ratings and review, consider it submitted
    //     if (hasProductRatings && hasReview) {
    //         setIsSubmitted(true);
    //         setIsRatingSubmitted(true);
    //     }
    // }, [productDetails, prev_rating, prev_review, hasProductRatings, hasReview]);

    // ============================================
    // NEW SIMPLIFIED useEffect - ONLY OVERALL REVIEW
    // ============================================
    const hasReview = prev_review && prev_review.trim() !== '';
    const hasRating = prev_rating > 0;

    useEffect(() => {
        // Set initial overall rating if available
        if (prev_rating && prev_rating > 0) {
            setRating(prev_rating);
            setSelectedStar(prev_rating);
            setIsRatingSubmitted(true);
        }

        // Set initial review text if available
        if (prev_review && prev_review.trim() !== '') {
            setReviewText(prev_review);
            setIsReviewSubmitted(true);
        }

        // If both rating and review are already submitted, mark as complete
        if (hasRating && hasReview) {
            setIsSubmitted(true);
        }
    }, [prev_rating, prev_review, hasRating, hasReview]);

    // ============================================
    // PREVIOUS HANDLERS - COMMENTED FOR REFERENCE
    // ============================================
    // const handleProductRatingChange = (productId, value) => {
    //     if (isSubmitted || isRatingSubmitted) return;
    //     // Update productRatings state
    //     setProductRatings(prev => {
    //         const updatedRatings = { ...prev, [productId]: value };
    //         // Calculate average rating
    //         const totalRating = Object.values(updatedRatings).reduce((acc, curr) => acc + curr, 0);
    //         const avgRating = Math.round(totalRating / Object.keys(updatedRatings).length);
    //         // Update overall rating
    //         setRating(avgRating);
    //         setSelectedStar(avgRating);
    //         return updatedRatings;
    //     });
    // };

    // ============================================
    // NEW SIMPLIFIED HANDLERS - OVERALL ONLY
    // ============================================
    const handleStarClick = (value) => {
        if (isSubmitted || isRatingSubmitted) return;
        setSelectedStar(value);
        setRating(value);
    };

    const handleReviewChange = (e) => {
        if (isSubmitted) return;
        setReviewText(e.target.value);
    };

    // ============================================
    // NEW SIMPLIFIED SUBMIT - OVERALL REVIEW ONLY
    // ============================================
    const handleSubmitReview = () => {
        // Prepare payload with ONLY overall rating and review
        const payload = {
            itemId,
            overallRating: rating > 0 ? rating : null,
            review: reviewText.trim() || null,
            google_review_link
        };

        fetch(`${process.env.REACT_APP_BASE_URL}/review`, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: { 'Content-Type': 'application/json' },
        })
        .then(response => response.json())
        .then((data) => {
            if (data.status === 'success') {
                if (reviewText.trim() && rating > 0) {
                    Swal.fire('Success', 'Thanks for helping us improve! Your review is now submitted!', 'success');
                    setIsReviewSubmitted(true);
                    setIsRatingSubmitted(true);
                    setIsSubmitted(true);
                } else if (reviewText.trim()) {
                    Swal.fire('Review Submitted', 'Thanks for submitting the review!', 'success');
                    setIsReviewSubmitted(true);
                } else if (rating > 0) {
                    Swal.fire('Rating Submitted', 'Rating submitted successfully!', 'success');
                    setIsRatingSubmitted(true);
                }
            } else {
                Swal.fire('Error', data.message || 'There was a problem submitting. Please try again.', 'error');
            }
        })
        .catch(error => {
            console.error('Error submitting review:', error);
            Swal.fire('Error', 'There was a problem submitting. Please try again.', 'error');
        });
    };

    const starRatingStyle = {
        width: '70%',
        marginLeft: '20%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row-reverse',
        borderTopRightRadius: '75px',
        borderBottomRightRadius: '75px',
        marginBottom: '20px',
        border: 'none',
        position: 'relative',
        background: primary_color,
        boxShadow: '0 1px 1px hsla(0deg 0% 0%/0.075), 0 2px 2px hsla(0deg 0% 0%/0.075), 0 4px 4px hsla(0deg 0% 0%/0.075), 0 8px 8px hsla(0deg 0% 0%/0.075), 0 16px 16px hsla(0deg 0% 0%/0.075)',
    };

    const ratingValueStyle = {
        color: secondary_highlight_color,
        position: 'absolute',
        top: '-7px',
        left: '-61px',
        borderRadius: '50%',
        height: '79px',
        width: '79px',
        background: secondary_color,
        textAlign: 'center',
    };

    // ============================================
    // PREVIOUS JSX WITH PRODUCT RATINGS - COMMENTED
    // ============================================
    // The old JSX had product-specific ratings section.
    // That section has been removed. The new JSX below
    // shows ONLY overall review stars and review text.

    return (
        <div className="container">
            {/* Google Review Link (shown when available) */}
            {google_review_link && google_review_link.trim() !== '' && (
                <div className="row mb-3">
                    <div className="col-12 d-flex justify-content-center align-items-center">
                        <a href={google_review_link} target="_blank" rel="noopener noreferrer">
                            <img
                                src="/assets/images/google_review.png"
                                alt="Google Review"
                                style={{ maxWidth: '200px', cursor: 'pointer' }}
                            />
                        </a>
                    </div>
                </div>
            )}

            {/* Main Star Rating (shown when no Google review link exists) */}
            {(!google_review_link || google_review_link.trim() === '') && (
                <div className="row star-rating" style={starRatingStyle}>
                    <div className="col-12 d-flex justify-content-around" style={{ marginTop: '-5px' }}>
                        {[1, 2, 3, 4, 5].map((starValue) => (
                            <span
                                key={starValue}
                                className={starValue <= selectedStar ? 'star filled' : 'star'}
                                onClick={() => handleStarClick(starValue)}
                                style={(isSubmitted || isRatingSubmitted) ? { pointerEvents: 'none' } : {}}
                            >
                                &#9733;
                            </span>
                        ))}
                    </div>
                    <fieldset className="rating-container">
                        <div className="rating-value" style={ratingValueStyle}>
                            {selectedStar}
                        </div>
                    </fieldset>
                </div>
            )}

            {/* ============================================ */}
            {/* PREVIOUS PRODUCT-SPECIFIC RATINGS SECTION - COMMENTED OUT */}
            {/* ============================================ */}
            {/* {productDetails && productDetails.length > 0 && (
                <div className="row mt-3">
                    {productDetails.map((product) => (
                        <div key={product.id} className="col-12 mt-2">
                            <div className="d-flex align-items-center justify-content-between">
                                <h6>{product.product_name}</h6>
                                <div>
                                    {[1, 2, 3, 4, 5].map((starValue) => (
                                        <span
                                            key={starValue}
                                            className={starValue <= (productRatings[product.id] || 0) ? 'star filled' : 'star'}
                                            onClick={() => handleProductRatingChange(product.id, starValue)}
                                            style={
                                                isSubmitted || hasProductRatings 
                                                    ? { pointerEvents: 'none' } 
                                                    : {}
                                            }
                                        >
                                            &#9733;
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )} */}

            {/* Review textarea (shown when no review exists) */}
            {!isReviewSubmitted && !isSubmitted && (
                <div className="row mt-3">
                    <div className="col-12">
                        <textarea
                            rows="5"
                            placeholder={`How was your experience with ${branch_name}?`}
                            className="form-control"
                            value={reviewText}
                            onChange={handleReviewChange}
                            style={isReviewSubmitted ? { pointerEvents: 'none', background: '#eee' } : {}}
                        />
                    </div>
                </div>
            )}

            {/* Submit button (shown when not fully submitted) */}
            {!(isRatingSubmitted && isReviewSubmitted) && (
                <div className="col-12 mt-3">
                    <button
                        onClick={handleSubmitReview}
                        className="btn btn-primary w-100"
                        style={{ backgroundColor: primary_color, color: primary_highlight_color }}
                    >
                        Submit
                    </button>
                </div>
            )}

            {/* Conditional messages */}
            {isReviewSubmitted && !isRatingSubmitted && (
                <div className="row p-2">
                    <div className="col-12 text-center">
                        <p style={{
                            paddingTop: '10px',
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            fontFamily: 'serif',
                            color: '#666'
                        }}>
                            Thank you for your review! Now please submit your rating.
                        </p>
                    </div>
                </div>
            )}

            {!isReviewSubmitted && isRatingSubmitted && (
                <div className="row p-2">
                    <div className="col-12 text-center">
                        <p style={{
                            paddingTop: '10px',
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            fontFamily: 'serif',
                            color: '#666'
                        }}>
                            Thank you for your rating! Now please submit your review.
                        </p>
                    </div>
                </div>
            )}

            {/* Thank you message (shown when both rating and review are submitted) */}
            {isRatingSubmitted && isReviewSubmitted && (
                <div className='row p-2'>
                    <div className="review-section col-12">
                        <p style={{
                            paddingTop: '10px',
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            fontFamily: 'serif',
                            color: '#666'
                        }}>
                            "Thanks for helping us improve! Your review is now submitted!"
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewStarRating;